import {
  useCallback,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  casesStatusPresetOf,
  casesStatusPresets,
} from '../../utils';
import useAuth from '../auth/useAuth';

const parseTeams = (param) => {
  const gpids = (param ?? '').split(',').filter(Boolean);
  return (gpids.length) ? gpids : null;
};

const parseStatuses = (param) => {
  const kinds = (param ?? '').split(',').filter((kind) => casesStatusPresets.kinds.includes(kind));
  return (kinds.length) ? kinds : casesStatusPresets.active;
};

// A list's filters resolve URL first, saved setting next: a link can carry a
// one-off selection (?<teamsParam>=<gpid,gpid>&<statusesParam>=<kind,kind>, e.g.
// from a dashboard card), else the viewer's resting choice applies
// (settings.user[settingKey]: standings for every customer, teams per customer),
// else the defaults: no team beyond the page's own and the automatic standings.
// Applying a choice saves it and drops the URL override, so the URL never
// outlives what the viewer picked. teamsValue and statusesValue are the raw
// strings, stable across renders, for callers keeping them in effect and
// callback dependencies
const useSavedListFilter = ({ settingKey, teamsParam, statusesParam }) => {
  const { auth } = useAuth();
  const { info, addUserSetting } = auth;
  const cuid = info?.cuid;
  const [searchParams, setSearchParams] = useSearchParams();

  const saved = info?.settings?.user?.[settingKey];
  const teamsValue = searchParams.get(teamsParam) ?? saved?.teams?.[cuid]?.join(',') ?? null;
  const statusesValue = searchParams.get(statusesParam) ?? saved?.statuses?.join(',') ?? null;
  const teams = useMemo(() => parseTeams(teamsValue), [teamsValue]);
  const statuses = useMemo(() => parseStatuses(statusesValue), [statusesValue]);

  // Saves the resting filter (undefined keeps a part, an empty array or the
  // automatic preset drops it) and lifts any URL override, leaving the rest of
  // the URL alone (the open item lives there too)
  const setFilter = useCallback(
    (next) => {
      const current = info?.settings?.user?.[settingKey] ?? {};
      const setting = { ...current };
      if (next.statuses !== undefined) {
        if (next.statuses?.length && (casesStatusPresetOf(next.statuses) !== 'active')) {
          setting.statuses = next.statuses;
        } else delete setting.statuses;
      }
      if (next.teams !== undefined) {
        const teams = { ...(current.teams ?? {}) };
        if (next.teams?.length) teams[cuid] = next.teams;
        else delete teams[cuid];
        if (Object.keys(teams).length) setting.teams = teams;
        else delete setting.teams;
      }
      addUserSetting({ [settingKey]: setting });

      setSearchParams(
        (params) => {
          const nextParams = new URLSearchParams(params);
          nextParams.delete(teamsParam);
          nextParams.delete(statusesParam);
          return nextParams;
        },
        { replace: true },
      );
    },
    [info, cuid, settingKey, teamsParam, statusesParam, addUserSetting, setSearchParams],
  );

  return {
    teams,
    statuses,
    teamsValue,
    statusesValue,
    setFilter,
  };
};

export default useSavedListFilter;
